import {
  Component, computed, input,
  OnDestroy,
  OnInit,
  output, signal, ViewChild,
} from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatMenuTrigger } from '@angular/material/menu'
import {
  AppointmentModel,
  AppointmentSelectOption,
  ChargesFormArray,
  ClinicService,
  Estimate,
  EstimateCharge,
  EstimateData,
  EstimateFormGroup,
  EstimateService,
  Facility,
  FacilityService,
  getSelectedPhysicianIdFromAppointments,
} from '@mmx/shared'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil, tap } from 'rxjs/operators'

import { EstimateTemplateDialogComponent } from '../estimate-template-dialog/estimate-template-dialog.component'

@Component({
  selector: 'mmx-estimate-charges',
  templateUrl: './estimate-charges.component.html',
  styleUrls: ['./estimate-charges.component.scss'],
  standalone: false,
})
export class EstimateChargesComponent implements OnDestroy, OnInit {
  @ViewChild('menuTrigger') menuTrigger?: MatMenuTrigger
  readonly estimate = input.required<EstimateData>()
  readonly estimateForm = input.required<EstimateFormGroup>()
  readonly estimateChargesLoading = input.required<boolean>()
  readonly claimSearchEnabled = input.required<boolean>()
  readonly appointmentOptions = input.required<AppointmentSelectOption[]>()
  readonly appointmentIdsSignal = signal<string[]>([])
  readonly appointmentOptionsView = computed(() =>
  {
    return this.appointmentOptions().map(o => ({
      ...o,
      selected: this.appointmentIdsSignal().includes(o.appointment.id),
    }))},
  )
  readonly selectedPhysicianId = computed(() => getSelectedPhysicianIdFromAppointments(
    this.appointmentOptions().map(option => option.appointment),
    this.estimateForm().controls.appointmentIds.value ?? [],
  ))
  readonly allAppointments = input.required<AppointmentModel[]>()
  readonly shopper = input(false)
  readonly resetEstimateForm = output<{
    type: string
    estimate: EstimateData
  }>()
  readonly claimAdded = output<{ charges: EstimateCharge[] }>()
  readonly addChargeDiscountControl = output<{ index: number; type: string }>()
  readonly removeChargeDiscountControl = output<{ index: number }>()
  readonly loadTemplate = output<string>()
  readonly facilityChanged = output<string | null>()
  readonly chargesCleared = output<void>()
  readonly templateSaved = output<string>()
  readonly dirty = output<void>()
  readonly addChargesRequested = output<{ reset?: boolean }>()
  readonly estimateTemplatesEnabled = signal<boolean>(true)

  get chargesFormArray(): ChargesFormArray {
    return this.estimateForm().get('charges') as ChargesFormArray
  }

  get canSaveTemplate(): boolean {
    const hasCharges = (this.estimate()?.charges ?? []).length > 0
    const hasFacility =
      !!this.estimateForm().get('facilityId')?.value ||
      !!this.estimate()?.facilityId
    return hasCharges && hasFacility
  }

  private onDestroy$ = new Subject<void>()
  location?: Facility

  PATIENT_TYPE = Estimate.PATIENT_TYPE
  modelClaimSearchQuery$ = this.estimateService.modelClaimSearchQuery$

  constructor(
    private dialog: MatDialog,
    private estimateService: EstimateService,
    private clinicService: ClinicService,
    private facilityService: FacilityService,
  ) {}

  ngOnInit() {
    // Subscribe to clinic$ rather than reading the static snapshot — a deep
    // link or role switch can land here before clinic has loaded, leaving the
    // feature flag stuck at false until next nav.
    this.clinicService.clinic$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(clinic => {
        this.estimateTemplatesEnabled.set(!!clinic?.modulesPreferences?.estimateTemplates)
      })

    const initialFacility = this.estimateForm().get('facilityId')?.value || this.estimate()?.facilityId
    this.onFacilityChange(initialFacility)

    this.estimateForm()
      .get('facilityId')
      ?.valueChanges.pipe(
        debounceTime(100),
        takeUntil(this.onDestroy$),
        tap((value) => {
          this.onFacilityChange(value)
        }),
      )
      .subscribe()

    this.appointmentIdsSignal.set(
      this.estimateForm().controls.appointmentIds.value ?? [],
    )

    this.estimateForm().controls.appointmentIds.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(ids => this.appointmentIdsSignal.set(ids))

    this.estimateService.appointmentsChanged$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        error: () => alert('Failed to create charges from appointment'),
      })
  }

  onFacilityChange(value: string | Facility | undefined) {
    if (!value) {
      const control = this.estimateForm().get('facilityId')
      control?.setValue(null)
      control?.markAsTouched()
      this.location = undefined
      this.facilityChanged.emit(null)
      return
    }

    const id = typeof value === 'string' ? value : value?.id
    if (id) {
      const facility = this.facilityService.allFacilities.find(
        (f) => f.id === id,
      )
      if (facility) {
        this.location = facility
        this.facilityChanged.emit(facility.id)
      }
    }
  }

  addClaim(claim: { charges: EstimateCharge[] }) {
    if (claim?.charges?.length) {
      this.claimAdded.emit(claim)
    }
  }

  onAddChargeDiscountControl(event: { index: number; type: string }) {
    this.addChargeDiscountControl.emit(event)
  }

  onRemoveChargeDiscountControl(event: { index: number }) {
    this.removeChargeDiscountControl.emit(event)
  }

  resetEstimateFormEvent(type: string) {
    this.resetEstimateForm.emit({ type, estimate: this.estimate() })
  }

  onTemplateSelected(id: string) {
    this.loadTemplate.emit(id)
    this.menuTrigger?.closeMenu()
  }

  openSaveTemplateDialogEvent() {
    const selectedFacilityId = this.estimateForm().get('facilityId')?.value
    const facility =
      this.location ||
      this.facilityService.allFacilities.find(
        (f) => f.id === selectedFacilityId,
      )
    if (!facility) {
      return
    }
    this.dialog
      .open(EstimateTemplateDialogComponent, {
        data: {
          facility,
          estimateTemplate: {
            facilityId: facility.id,
            charges: this.estimate().charges,
          },
        },
        width: '900px',
        panelClass: 'dialog-form',
      })
      .afterClosed()
      .subscribe((template) => {
        if (template?.id) {
          this.templateSaved.emit(template.id)
        }
      })
  }

  markDirty() {
    this.dirty.emit()
  }

  deleteAllChargesEvent() {
    this.chargesCleared.emit()
    this.menuTrigger?.closeMenu()
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  addChargesByAppointments(reset?: boolean) {
    this.addChargesRequested.emit({ reset })
  }
}
